import React from 'react';
import clsx from 'clsx';
import Button from './Button';

/**
 * The shape a caller describes an empty result with.
 *
 * This lived inline in `Table` - which meant every other surface that had
 * nothing to show (a pane, a grid, a picker, a canvas tab) either invented
 * its own markup or reached into the table for it. It is the same three
 * things everywhere: a glyph, a headline, a line of explanation, and at most
 * one way out.
 */
export interface EmptyStateProps {
  icon?: React.ReactNode;
  headline?: string;
  subtext?: string;
  button?: {
    label: string;
    onClick: () => void;
  };
  /** Vertical breathing room. `compact` for panes, `default` for a page/table. */
  density?: 'compact' | 'default';
  className?: string;
}

/**
 * THE empty state. Use it wherever a list, pane or canvas has nothing in it —
 * never a bare "No results" string, never a hand-rolled centered div.
 *
 *   <EmptyState
 *     icon={<PiFolderLight />}
 *     headline={t('No matters yet')}
 *     subtext={t('Create one to get started.')}
 *     button={{ label: t('New matter'), onClick: openCreate }}
 *   />
 *
 * `Table` renders this for its own `emptyState` prop, so the two can never
 * drift apart again.
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  headline,
  subtext,
  button,
  density = 'default',
  className,
}) => (
  <div
    data-testid="empty-state"
    className={clsx(
      'flex flex-col items-center gap-4 max-w-md mx-auto text-center',
      density === 'compact' ? 'py-10' : 'py-28',
      className
    )}
  >
    {icon && (
      <div className="text-fg-muted">
        {React.isValidElement(icon)
          ? React.cloneElement(icon as React.ReactElement<any>, { size: 48 })
          : icon}
      </div>
    )}
    {headline && <h2 className="text-lg text-fg-strong">{headline}</h2>}
    {subtext && <p className="text-sm text-fg-muted text-center">{subtext}</p>}
    {button && (
      <Button variant="primary" size="small" onClick={button.onClick} className="mt-4">
        {button.label}
      </Button>
    )}
  </div>
);

export default EmptyState;
